import React, { useState, useEffect, useMemo } from "react";
import { Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Paper, CircularProgress, IconButton, Typography, TextField, MenuItem, Select, FormControl, InputLabel, Button, Dialog, DialogTitle, DialogContent, Card, CardContent, Grid, Avatar, Chip, Divider, Stack } from "@mui/material";
import { Visibility as VisibilityIcon, RestartAlt, Summarize as SummarizeIcon, Close as CloseIcon, Payments as PaymentsIcon, AccountBalanceWallet as AccountBalanceWalletIcon, SearchOff as NoDataIcon } from "@mui/icons-material";
import { getData } from "../../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../../services/endpoints";

function CompensationStepper({ batchData, payPeriod }) {
    const [compensationList, setCompensationList] = useState([]);
    const [loading, setLoading] = useState(false);

    // Breakdown Dialog States
    const [batchDetailOpen, setBatchDetailOpen] = useState(false);
    const [selectedBatchDetail, setSelectedBatchDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Pagination & Filter States
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filters, setFilters] = useState({ search: "", type: "All" });

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const hrId = userData?.id;

    // Fetch Table Data
    useEffect(() => {
        const fetchCompensationData = async () => {
            if (!hrId) return;
            setLoading(true);
            try {
                const response = await getData(API_ENDPOINTS.getPayrollsByHrId(hrId));
                if (response?.result) {
                    setCompensationList(response.result);
                }
            } catch (error) {
                setCompensationList([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCompensationData();
    }, [hrId, batchData]);

    // --- Action: View Breakdown ---
    const handleViewBreakdown = async (emp_id) => {
        const batchRow = batchData.find(b => b.empId === emp_id);
        if (!batchRow?.id) {
            alert("No batch record found for this employee.");
            return;
        }

        setBatchDetailOpen(true);
        setDetailLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.getBatchPayrollById(batchRow.id));
            if (res?.statusCode === 200) {
                setSelectedBatchDetail(res.result);
            }
        } catch (err) {
            setSelectedBatchDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    // --- Filter Logic ---
    const filteredEmployees = useMemo(() => {
        if (!compensationList) return [];
        return compensationList.filter((emp) => {
            const matchesSearch =
                emp.emp_id?.toLowerCase().includes(filters.search.toLowerCase()) ||
                emp.first_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
                emp.last_name?.toLowerCase().includes(filters.search.toLowerCase());
            const matchesType = filters.type === "All" || emp.employmentType === filters.type;
            return matchesSearch && matchesType;
        });
    }, [compensationList, filters]);

    const handleReset = () => {
        setFilters({ search: "", type: "All" });
        setPage(0);
    };

    const DynamicRow = ({ label, value, isBold = false }) => {
        const numericValue = parseFloat(value || 0);
        if (numericValue <= 0) return null;

        return (
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell sx={{ py: 1, border: 0, color: 'text.secondary' }}>{label}</TableCell>
                <TableCell align="right" sx={{ py: 1, border: 0, fontWeight: isBold ? 700 : 500 }}>
                    ₹{numericValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </TableCell>
            </TableRow>
        );
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={40} /></Box>;

    return (
        <Box>
            <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                    <TextField
                        fullWidth
                        size="small" label="Search"
                        value={filters.search}
                        placeholder="Search employee by ID or name"
                        onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(0); }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Employee Type</InputLabel>
                        <Select
                            value={filters.type}
                            label="Employee Type"
                            onChange={(e) => { setFilters({ ...filters, type: e.target.value }); setPage(0); }}
                        >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="Full-time">Full-time</MenuItem>
                            <MenuItem value="Part-time">Part-time</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                    <Button fullWidth startIcon={<RestartAlt />} onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>Reset</Button>
                </Grid>
            </Grid>

            {/* --- Table Section --- */}
            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', minHeight: filteredEmployees.length === 0 ? '300px' : 'auto', display: 'flex', flexDirection: 'column' }}>
                <Table sx={{ minWidth: 900 }}>
                    <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee ID</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Name</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Job Title</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Gross</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Net Salary</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Total Deductions</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Annual CTC</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                        {filteredEmployees.length > 0 ? (
                            filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.emp_id} hover sx={{
                                    '&:hover': {
                                        bgcolor: '#F1F5F9 !important'
                                    }
                                }}>
                                    <TableCell sx={{ color: '#1E293B' }}>{row.emp_id}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>{`${row.first_name} ${row.last_name || ""}`}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>{row.designation}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }} align="right">₹{Number(row.grossEarningsMonthly).toLocaleString()}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }} align="right">₹{Number(row.netSalaryMonthly).toLocaleString()}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }} align="right">₹{Number(row.totalDeductionsMonthly).toLocaleString()}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }} align="right">₹{Number(row.annualCTC).toLocaleString()}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }} align="center">
                                        <IconButton color="primary" onClick={() => handleViewBreakdown(row.emp_id)}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                                    <NoDataIcon sx={{ fontSize: 60, color: '#CBD5E1', mb: 1 }} />
                                    <Typography variant="h6" color="textSecondary">No Employees Found</Typography>
                                    <Typography variant="body2" color="textSecondary">Try adjusting your search or filters</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

            </TableContainer>

            <TablePagination
                component="div" count={filteredEmployees.length}
                rowsPerPage={rowsPerPage} page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />

            {/* --- Breakdown Dialog --- */}
            <Dialog
                open={batchDetailOpen}
                onClose={() => setBatchDetailOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' } }}
            >
                <DialogTitle sx={{ background: 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)', color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", py: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <SummarizeIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Payroll Breakdown</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>{selectedBatchDetail?.month} {selectedBatchDetail?.year} Cycle</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => setBatchDetailOpen(false)} sx={{ color: "white" }}><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3, bgcolor: "#f8f9fa" }}>
                    {detailLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 10 }}><CircularProgress size={50} sx={{ mb: 2 }} /><Typography color="textSecondary">Calculating Components...</Typography></Box>
                    ) : selectedBatchDetail ? (
                        <Box>
                            <Card elevation={0} sx={{ borderRadius: 3, my: 3, border: "1px solid #e0e6ed", bgcolor: '#fff' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1a237e', width: 56, height: 56, fontWeight: 'bold' }}>{selectedBatchDetail.name.charAt(0)}</Avatar>
                                            <Box><Typography variant="subtitle1" fontWeight={800}>{selectedBatchDetail.name}</Typography><Typography variant="body2" color="textSecondary">ID: {selectedBatchDetail.empId}</Typography></Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: { sm: 'right' } }}>
                                            <Chip label={selectedBatchDetail.payed_flag ? "Paid" : "Pending"} color={selectedBatchDetail.payed_flag ? "success" : "warning"} sx={{ fontWeight: 700, borderRadius: 1.5 }} />
                                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>Working Days: <b>{selectedBatchDetail.workingDays}</b></Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            <Grid container spacing={3} alignItems="stretch">
                                <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}><PaymentsIcon fontSize="small" /> EARNINGS</Typography>
                                        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#fff', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Table size="small">
                                                <TableBody>
                                                    <DynamicRow label="Basic Pay" value={selectedBatchDetail.basicPay} />
                                                    <DynamicRow label="HRA" value={selectedBatchDetail.hra} />
                                                    <DynamicRow label="Conveyance" value={selectedBatchDetail.conveyanceAllowance} />
                                                    <DynamicRow label="Special Allowance" value={selectedBatchDetail.specialAllowance} />
                                                    <DynamicRow label="Other Allowances" value={selectedBatchDetail.otherAllowance} />
                                                    <DynamicRow label="Leave Encashment" value={selectedBatchDetail.leaveEncashmentAmount} />
                                                </TableBody>
                                            </Table>
                                            <Box sx={{ mt: 'auto', pt: 2 }}><Divider sx={{ mb: 1.5 }} /><Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}><Typography variant="subtitle1" fontWeight={800} color="#1a237e">Gross Earnings</Typography><Typography variant="subtitle1" fontWeight={800} color="#1a237e">₹{parseFloat(selectedBatchDetail.totalGross).toLocaleString()}</Typography></Box></Box>
                                        </Paper>
                                    </Box>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}><AccountBalanceWalletIcon fontSize="small" /> DEDUCTIONS</Typography>
                                        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#fff', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Table size="small">
                                                <TableBody>
                                                    <DynamicRow label="Provident Fund (PF)" value={selectedBatchDetail.pfEmployee} />
                                                    <DynamicRow label="ESI" value={selectedBatchDetail.esiEmployee} />
                                                    <DynamicRow label="Professional Tax (PT)" value={selectedBatchDetail.professionalTax} />
                                                    <DynamicRow label="Income Tax (TDS)" value={selectedBatchDetail.incomeTax} />
                                                    <DynamicRow label="Education Cess" value={selectedBatchDetail.cess} />
                                                    <DynamicRow label="LOP Deduction" value={selectedBatchDetail.lopAmount} />
                                                </TableBody>
                                            </Table>
                                            <Box sx={{ mt: 'auto', pt: 2 }}><Divider sx={{ mb: 1.5 }} /><Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}><Typography variant="subtitle1" fontWeight={800} color="#d32f2f">Total Deductions</Typography><Typography variant="subtitle1" fontWeight={800} color="#d32f2f">₹{parseFloat(selectedBatchDetail.totalDeductions).toLocaleString()}</Typography></Box></Box>
                                        </Paper>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 4, p: 3, borderRadius: 4, background: 'linear-gradient(90deg, #1a237e 0%, #311b92 100%)', color: 'white', boxShadow: '0 10px 20px -5px rgba(26, 35, 126, 0.4)' }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, sm: 5 }}>
                                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1.2, opacity: 0.8 }}>Net Take Home (Final)</Typography>
                                        <Typography variant="h4" fontWeight={800} sx={{ color: '#4caf50' }}>₹{parseFloat(selectedBatchDetail.netSalary).toLocaleString()}</Typography>
                                    </Grid>
                                    <Grid size={{ md: 1 }} sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'center' }}><Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', height: '60px', mx: 'auto' }} /></Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Stack direction="row" justifyContent="space-between" spacing={3}>
                                            {parseFloat(selectedBatchDetail.lopAmount) > 0 && (<Box><Typography variant="caption" sx={{ color: '#ff8a80', fontWeight: 700, display: 'block' }}>(-) LOP ({selectedBatchDetail.lopCount} Days)</Typography><Typography variant="h6" fontWeight={700}>₹{parseFloat(selectedBatchDetail.lopAmount).toLocaleString()}</Typography></Box>)}
                                            {parseFloat(selectedBatchDetail.reimbursementAmount) > 0 && (<Box sx={{ textAlign: 'right', ml: 'auto' }}><Typography variant="caption" sx={{ color: '#b9f6ca', fontWeight: 700, display: 'block' }}>(+) REIMBURSEMENTS</Typography><Typography variant="h6" fontWeight={700}>₹{parseFloat(selectedBatchDetail.reimbursementAmount).toLocaleString()}</Typography></Box>)}
                                            {parseFloat(selectedBatchDetail.lopAmount) === 0 && parseFloat(selectedBatchDetail.reimbursementAmount) === 0 && (<Typography variant="body2" sx={{ opacity: 0.7, fontStyle: 'italic' }}>No adjustments this cycle.</Typography>)}
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    ) : null}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default CompensationStepper;